import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CancelButton, CloseButton, SaveChangesButton } from '@/styles/StandardButtons';
import { GenericForm, GenericSelect, RequiredMark } from '@/styles/GenericForm';
import { colors } from '@/design-system/colors';
import type { DriveFolderNode, DriveItem } from '../types';
import { buildFolderOptions } from '../lib/drive-items';

const ROOT_VALUE = '__drive_root__';

interface ShellProps {
  title: string;
  submitText: string;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}

/** Every Drive dialog is the generic KrewUp form (overlay, header, Cancel / action footer); only its body differs. */
function DialogShell({ title, submitText, saving, error, onCancel, onSubmit, children }: ShellProps) {
  return (
    <GenericForm
      title={title}
      mode="edit"
      fields={[]}
      isOpen
      onSubmit={() => undefined}
      onCancel={onCancel}
      size="compact"
      customLayout={
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="generic-form-container w-full max-w-md"
          style={{ maxHeight: 'var(--form-max-height-compact)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="generic-form-header generic-form-header-compact" style={{ backgroundColor: colors.formHeaderFooter }}>
            <h2 className="font-form-title">{title}</h2>
            <CloseButton onClick={onCancel} />
          </div>
          <div className="generic-form-body generic-form-body-compact space-y-3">
            {children}
            {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          </div>
          <div className="generic-form-footer generic-form-footer-compact" style={{ backgroundColor: colors.formHeaderFooter }}>
            <CancelButton onClick={onCancel} />
            <SaveChangesButton disabled={saving}>{saving ? 'Saving...' : submitText}</SaveChangesButton>
          </div>
        </form>
      }
    />
  );
}

function TextRow({ id, label, value, onChange, placeholder, autoFocus, required = true }: { id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; autoFocus?: boolean; required?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="font-form-label mb-0 w-24 shrink-0">{label}<RequiredMark required={required} /></Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} className="font-form-input generic-form-field" />
    </div>
  );
}

/** Runs a dialog action; the dialog closes on success and shows the server's message on failure. */
function useAction(run: () => Promise<unknown>, close: () => void) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await run();
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };
  return { saving, error, submit };
}

export function NewFolderDialog({ onSave, onCancel }: { onSave: (name: string) => Promise<unknown>; onCancel: () => void }) {
  const [name, setName] = useState('');
  const { saving, error, submit } = useAction(() => onSave(name.trim()), onCancel);
  return (
    <DialogShell title="New Folder" submitText="Create" saving={saving} error={error} onCancel={onCancel} onSubmit={() => name.trim() && submit()}>
      <TextRow id="drive-folder-name" label="Folder Name" value={name} onChange={setName} placeholder="Enter folder name" autoFocus />
    </DialogShell>
  );
}

export function AddUrlDialog({ onSave, onCancel }: { onSave: (name: string, url: string) => Promise<unknown>; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const { saving, error, submit } = useAction(() => onSave(name.trim(), url.trim()), onCancel);
  return (
    <DialogShell title="Add URL" submitText="Add" saving={saving} error={error} onCancel={onCancel} onSubmit={() => url.trim() && submit()}>
      <TextRow id="drive-url-name" label="Name" value={name} onChange={setName} placeholder="Optional" required={false} />
      <TextRow id="drive-url-value" label="URL" value={url} onChange={setUrl} placeholder="https://..." autoFocus />
    </DialogShell>
  );
}

export function RenameDialog({ item, onSave, onCancel }: { item: DriveItem; onSave: (name: string, url?: string) => Promise<unknown>; onCancel: () => void }) {
  const [name, setName] = useState(item.name);
  const [url, setUrl] = useState(item.url || '');
  const isLink = item.type === 'url';
  const { saving, error, submit } = useAction(() => onSave(name.trim(), isLink ? url.trim() : undefined), onCancel);
  const changed = name.trim() !== item.name || (isLink && url.trim() !== (item.url || ''));
  return (
    <DialogShell
      title={isLink ? 'Edit Link' : `Rename ${item.type === 'folder' ? 'Folder' : 'File'}`}
      submitText="Save Changes"
      saving={saving}
      error={error}
      onCancel={onCancel}
      onSubmit={() => (!changed ? onCancel() : name.trim() && (!isLink || url.trim()) && submit())}
    >
      <TextRow id="drive-item-name" label="Name" value={name} onChange={setName} autoFocus />
      {isLink && <TextRow id="drive-item-url" label="URL" value={url} onChange={setUrl} placeholder="https://..." />}
    </DialogShell>
  );
}

/** "Destination folder:" + the project's folders as an indented tree (and the Drive root). Nothing else. */
export function MoveDialog({
  ids,
  loadFolders,
  onSave,
  onCancel,
}: {
  ids: string[];
  loadFolders: () => Promise<DriveFolderNode[]>;
  onSave: (parentId: string | null) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [folders, setFolders] = useState<DriveFolderNode[] | null>(null);
  const [destination, setDestination] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const { saving, error, submit } = useAction(() => onSave(destination === ROOT_VALUE ? null : destination), onCancel);

  useEffect(() => {
    let alive = true;
    loadFolders()
      .then((list) => alive && setFolders(list))
      .catch((e) => alive && setLoadError(e instanceof Error ? e.message : 'Could not load the folders'));
    return () => {
      alive = false;
    };
  }, [loadFolders]);

  const options = useMemo(
    () => [{ value: ROOT_VALUE, label: 'Drive' }, ...buildFolderOptions(folders || [], ids)],
    [folders, ids]
  );

  return (
    <DialogShell title="Move" submitText="Save Changes" saving={saving} error={error || loadError} onCancel={onCancel} onSubmit={() => destination && submit()}>
      <div className="flex items-center gap-2">
        <Label htmlFor="drive-move-destination" className="font-form-label mb-0 shrink-0">Destination folder:</Label>
        <GenericSelect
          id="drive-move-destination"
          value={destination}
          onValueChange={setDestination}
          options={options}
          placeholder={folders ? 'Select folder' : 'Loading...'}
          required
          disabled={!folders}
          className="flex-1"
          contentClassName="max-h-[18.75rem] overflow-y-auto"
        />
      </div>
    </DialogShell>
  );
}
